import { ChevronRight, Cube, EyeOff } from "@procore/core-icons";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ListNode, Node, Tree } from "components/objectTree/types";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { useAbortController } from "hooks/useAbortController";
import { hideObjectHierarchy, showObjectHierarchy } from "utils/objectActions";
import { isContainerFileNode } from "utils/objectPath";
import { getGroupStateForIdSet } from "utils/treeHelpers";

import { ITEM_HEIGHT_IOS } from "../constants";
import { invertGroupState, useIsolationVisibility } from "../hooks/useIsolationVisibility";
import { formatNodeDisplayName } from "../utils/formatting";

export type MobileObjectTreeRowProps = {
    node: Node<ModelTreeNodeData>;
    tree: Tree<ModelTreeNodeData>;
    onDrillInto: (path: string) => void;
    onLeafTap: (node: Node<ModelTreeNodeData>) => void;
};

export function MobileObjectTreeRow({ node, tree, onDrillInto, onLeafTap }: MobileObjectTreeRowProps) {
    const { t } = useTranslation();
    const {
        state: { db },
    } = useExplorerGlobals(true);
    const hidden = useHidden();
    const dispatchHidden = useDispatchHidden();
    const isolation = useIsolationVisibility();
    const isolationVisibleIds = isolation.visibleIds;
    const [abortController] = useAbortController();
    const [isToggling, setIsToggling] = useState(false);

    const item = useMemo((): ListNode<ModelTreeNodeData> => ({ node, parent: null, level: 0 }), [node]);
    const hiddenState = useMemo(() => {
        if (isolationVisibleIds) {
            return invertGroupState(getGroupStateForIdSet(isolationVisibleIds, item, tree));
        }
        return getGroupStateForIdSet(hidden.ids, item, tree);
    }, [hidden.ids, isolationVisibleIds, item, tree]);
    const isHidden =
        hiddenState === "all" || (!isolationVisibleIds && !node.isLeaf && hidden.ids.has(node.data.object.id));

    const shouldShow = isHidden || hiddenState === "some";

    const handleToggle = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isToggling) {
            return;
        }
        if (shouldShow) {
            if (isolationVisibleIds) {
                await isolation.setHierarchyVisible({
                    nodes: [node.data.object],
                    visible: true,
                    db,
                    abortSignal: abortController.current.signal,
                    setLoading: setIsToggling,
                });
                return;
            }
            await showObjectHierarchy({
                nodes: [node.data.object],
                db,
                abortSignal: abortController.current.signal,
                setLoading: setIsToggling,
                dispatchHidden,
            });
        } else {
            if (isolationVisibleIds) {
                await isolation.setHierarchyVisible({
                    nodes: [node.data.object],
                    visible: false,
                    db,
                    abortSignal: abortController.current.signal,
                    setLoading: setIsToggling,
                });
                return;
            }
            await hideObjectHierarchy({
                nodes: [node.data.object],
                db,
                abortSignal: abortController.current.signal,
                setLoading: setIsToggling,
                dispatchHidden,
            });
        }
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (node.isLeaf) {
            onLeafTap(node);
        } else {
            onDrillInto(node.path);
        }
    };

    const displayName = formatNodeDisplayName(item);
    const contentOpacity = isHidden ? 0.4 : 1;

    return (
        <div
            onClick={handleClick}
            style={{
                display: "flex",
                alignItems: "center",
                minHeight: ITEM_HEIGHT_IOS,
                padding: "0 16px",
                gap: 10,
                borderBottom: "1px solid rgba(172, 181, 185, 0.32)",
                userSelect: "none",
            }}
        >
            {isContainerFileNode(node.path) && (
                <Cube style={{ width: 20, height: 20, flexShrink: 0, color: "#232729", opacity: contentOpacity }} />
            )}
            <span
                style={{
                    flex: 1,
                    minWidth: 0,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                    overflowWrap: "anywhere",
                    fontSize: 18,
                    fontWeight: 600,
                    color: "#171a1c",
                    letterSpacing: "-0.28px",
                    lineHeight: "20px",
                    opacity: contentOpacity,
                }}
            >
                {displayName}
            </span>
            {hiddenState === "some" && (
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                        flexShrink: 0,
                        paddingTop: 2,
                        paddingBottom: 2,
                        paddingLeft: 6,
                        paddingRight: 8,
                        borderRadius: 9999,
                        background: "#eef0f1",
                        border: "1px solid #acb5b9",
                    }}
                >
                    <EyeOff style={{ width: 12, height: 12, flexShrink: 0, color: "#5e696e" }} aria-hidden="true" />
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: "16px",
                            color: "#5e696e",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {t("someHidden")}
                    </span>
                </span>
            )}
            <button
                type="button"
                aria-label={shouldShow ? t("showObject") : t("hideObject")}
                onClick={(e) => void handleToggle(e)}
                style={{
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    flexShrink: 0,
                    opacity: isToggling ? 0.4 : 1,
                    pointerEvents: isToggling ? "none" : "auto",
                }}
                tabIndex={-1}
            >
                <TogglePill on={!isHidden && hiddenState !== "some"} />
            </button>
            {!node.isLeaf && <ChevronRight style={{ flexShrink: 0, width: 20, height: 20, color: "#ACB5B9" }} />}
        </div>
    );
}

function TogglePill({ on }: { on: boolean }) {
    return (
        <div
            style={{
                width: 44,
                height: 26,
                borderRadius: 13,
                backgroundColor: on ? "#007AFF" : "#E5E5EA",
                position: "relative",
                flexShrink: 0,
                transition: "background-color 0.2s ease",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    top: 2,
                    left: on ? 20 : 2,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    backgroundColor: "white",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                    transition: "left 0.2s ease",
                }}
            />
        </div>
    );
}
